import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { saveProfile, subscribeToProfile } from '../services/profileService';
import { db, auth } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import OrderTracking from './OrderTracking';
import Header from './Header';
import AddressPicker from './AddressPicker';
import AccountIndexPage from './AccountIndexPage';
import { useNavigationGuard, ROUTES } from '../navigation';

function ProfilePanel({ user, onClose, initialTab = 'profile', trackingOrderId, onOpenCart, cartCount, onClearTrackingId, onHelp, onLogout, onAddressPickerChange }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [status, setStatus] = useState('');
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState('');
  const [addressStatus, setAddressStatus] = useState('');
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [allOrders, setAllOrders] = useState([]);
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Note: AddressPicker handles its own back button via useMobileBack internally.
  // It calls onClose (which sets isAddingAddress=false) when back is pressed on the map phase.

  // Notify parent when address picker opens/closes (to hide bottom nav)
  useEffect(() => {
    if (onAddressPickerChange) onAddressPickerChange(isAddingAddress);
  }, [isAddingAddress, onAddressPickerChange]);

  // Navigation guard for unsaved address changes
  useNavigationGuard({
    id: 'profile-address-editing',
    isActive: isAddingAddress && isSavingAddress,
    message: 'Your address is being saved. Are you sure you want to leave?',
  });

  // Phone is always from logged-in user (read-only)
  const phone = user?.phoneNumber || '';

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!user?.uid) return () => {};
    const unsub = subscribeToProfile(user.uid, (data) => {
      if (!data) return;
      setName(data.name || '');
      setAddresses(data.addresses?.length ? data.addresses : []);
    });
    return () => unsub();
  }, [user]);


  useEffect(() => {
    if (!user?.uid) return () => {};
    const ordersQuery = query(collection(db, 'orders'), where('userId', '==', user.uid));
    const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
      const ordersList = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const sorted = ordersList.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      setAllOrders(sorted);
      if (trackingOrderId) {
        const order = ordersList.find(o => o.id === trackingOrderId);
        if (order) setTrackingOrder(order);
      }
    });
    return () => unsubOrders();
  }, [user, trackingOrderId]);

  const handleStartEditName = () => {
    setEditNameValue(name);
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!editNameValue.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      setError('');
      setStatus('Saving...');
      await saveProfile(user.uid, {
        name: editNameValue.trim(),
        phone: phone,
        addresses,
      });
      setName(editNameValue.trim());
      setIsEditingName(false);
      setStatus('');
    } catch (err) {
      setError(err.message || 'Failed to save.');
      setStatus('');
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditNameValue('');
    setError('');
  };


  const addAddress = () => {
    if (addresses.length >= 10) return;
    setEditingIndex(null);
    setIsAddingAddress(true);
  };

  const handleEditAddress = (index) => {
    setEditingIndex(index);
    setIsAddingAddress(true);
  };

  const handlePickerSave = async (addressData) => {
    if (addresses.length >= 10 && editingIndex === null) {
      // AddressPicker prevents save when limit reached; this is a safety guard
      return;
    }

    let updatedAddresses;
    if (editingIndex !== null) {
      updatedAddresses = addresses.map((item, idx) =>
        idx === editingIndex ? addressData : item
      );
    } else {
      updatedAddresses = [...addresses, addressData];
    }

    try {
      setIsSavingAddress(true);
      await saveProfile(user.uid, {
        name: name.trim(),
        phone: phone.trim(),
        addresses: updatedAddresses,
      });
      setAddresses(updatedAddresses);
      setAddressStatus('✓ Saved!');
      await new Promise((resolve) => setTimeout(resolve, 500));
      setIsSavingAddress(false);
      setTimeout(() => {
        setIsAddingAddress(false);
        setEditingIndex(null);
        setAddressStatus('');
      }, 1000);
    } catch (err) {
      console.error('Failed to save address:', err);
      setAddressStatus('');
      setIsSavingAddress(false);
    }
  };

  const handleCloseAddAddress = () => {
    setAddressStatus('');
    setIsAddingAddress(false);
    setEditingIndex(null);
  };

  // Delete address handler
  const handleDeleteAddress = async (index) => {
    setDeleteConfirmIndex(index);
  };

  const confirmDeleteAddress = async () => {
    if (deleteConfirmIndex === null) return;
    
    const updatedAddresses = addresses.filter((_, i) => i !== deleteConfirmIndex);
    setAddresses(updatedAddresses);
    try {
      await saveProfile(user.uid, {
        name,
        phone,
        addresses: updatedAddresses,
      });
      setDeleteConfirmIndex(null);
    } catch (err) {
      console.error('Failed to delete address:', err);
      setDeleteConfirmIndex(null);
    }
  };

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    if (onLogout) {
      onLogout();
    } else {
      try {
        await signOut(auth);
        onClose();
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }
  };

  // Navigation handlers for the new account index page
  const handleNavigateToTerms = () => navigate(ROUTES.TERMS);
  const handleNavigateToPrivacy = () => navigate(ROUTES.PRIVACY);
  const handleNavigateToAddresses = () => {
    navigate(ROUTES.ADDRESSES);
  };
  const handleNavigateToOrders = () => {
    navigate(ROUTES.ORDERS);
  };
  const handleNavigateToHelp = () => {
    if (onHelp) onHelp();
  };
  const handleNavigateToShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Flash - Fast Delivery',
        text: 'Get groceries delivered in 15-45 minutes!',
        url: window.location.origin,
      }).catch(() => {});
    }
  };

  if (isSavingAddress) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'white'
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '30px',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          <div style={{
            fontSize: '60px',
            animation: 'bounce 1s ease-in-out infinite'
          }}>
            📍
          </div>
        </div>
        <h2 style={{ fontSize: '28px', marginBottom: '12px', fontWeight: '600', animation: 'fadeIn 0.5s ease-out' }}>Saving Address...</h2>
        <p style={{ fontSize: '16px', opacity: 0.9, animation: 'fadeIn 0.5s ease-out 0.2s both' }}>Please wait a moment</p>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          @keyframes fadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  if (addressStatus) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'white'
      }}>
        <div style={{
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '30px',
          animation: 'circleScale 0.5s ease-out'
        }}>
          <svg width="80" height="80" viewBox="0 0 52 52" style={{ animation: 'checkmarkDraw 0.8s ease-out 0.3s forwards' }}>
            <circle cx="26" cy="26" r="25" fill="none" stroke="white" strokeWidth="3" style={{ animation: 'circleDraw 0.6s ease-out forwards' }}/>
            <path fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" d="M14 27l7.5 7.5L38 18" style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'checkDraw 0.5s ease-out 0.5s forwards' }}/>
          </svg>
        </div>
        <h2 style={{ fontSize: '28px', marginBottom: '12px', fontWeight: '600', animation: 'fadeInUp 0.6s ease-out 0.8s both' }}>Address Saved!</h2>
        <p style={{ fontSize: '16px', opacity: 0.9, animation: 'fadeInUp 0.6s ease-out 1s both' }}>Your address has been saved successfully</p>
        <style>{`
          @keyframes circleScale {
            0% { transform: scale(0); opacity: 0; }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); opacity: 1; }
          }
          @keyframes circleDraw {
            0% { stroke-dasharray: 0 157; }
            100% { stroke-dasharray: 157 157; }
          }
          @keyframes checkDraw {
            0% { stroke-dashoffset: 48; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // Show Account Index Page when activeTab is 'profile' and not in a specific sub-view
  if (activeTab === 'profile' && !isAddingAddress && !trackingOrder) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#f8fafc', overflow: 'hidden' }}>
        <Header
          showSearch={false}
          isSignedIn={true}
          userName={name}
          userPhone={phone}
          onLogoClick={null}
          onOpenOrders={handleNavigateToOrders}
          onOpenAddresses={handleNavigateToAddresses}
          onOpenCart={onOpenCart}
          cartCount={cartCount}
          onLogout={handleLogout}
          onHelp={onHelp}
          showBackButton={false}
          showLocationSelector={false}
        />
        <AccountIndexPage
          onClose={onClose}
          onNavigateToOrders={handleNavigateToOrders}
          onNavigateToAddresses={handleNavigateToAddresses}
          onNavigateToPrivacy={handleNavigateToPrivacy}
          onNavigateToTerms={handleNavigateToTerms}
          onNavigateToHelp={handleNavigateToHelp}
          onNavigateToShare={handleNavigateToShare}
          onLogout={handleLogout}
          userName={name}
          userPhone={phone}
          userId={user?.uid}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#f8fafc', overflow: 'hidden' }}>
      {!isAddingAddress && <Header
        showSearch={false}
        isSignedIn={true}
        userName={name}
        userPhone={phone}
        onLogoClick={null}
        onOpenOrders={() => navigate(ROUTES.ORDERS)}
        onOpenAddresses={() => navigate(ROUTES.ADDRESSES)}
        onOpenCart={onOpenCart}
        cartCount={cartCount}
        onLogout={handleLogout}
        onHelp={onHelp}
        showBackButton={activeTab === 'orders' || activeTab === 'addresses'}
        onBackClick={() => navigate(ROUTES.ACCOUNT)}
        showLocationSelector={false}
      />}

      <div style={{
        flex: 1,
        overflowY: activeTab === 'orders' ? 'hidden' : 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        paddingBottom: activeTab === 'orders' ? 0 : 'calc(24px + env(safe-area-inset-bottom, 0px))',
        display: 'flex',
        flexDirection: 'column',
        padding: activeTab === 'orders' ? 0 : '16px',
      }}>
        {/* Profile Tab - shows name, phone, addresses, orders, and help */}
        {activeTab === 'profile' && (
          <div className="profile-overview">

            {/* Enhanced Profile card with modern design */}
            <div style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
              borderRadius: '24px',
              padding: '32px 24px 28px',
              textAlign: 'center',
              marginBottom: '28px',
              boxShadow: '0 8px 24px rgba(255,90,31,0.3), 0 2px 8px rgba(255,90,31,0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Decorative background pattern */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                right: '-20%',
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                filter: 'blur(40px)',
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-30%',
                left: '-10%',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.06)',
                filter: 'blur(30px)',
              }} />
              
              {/* Avatar with enhanced styling */}
              <div style={{
                width: '88px',
                height: '88px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '38px',
                color: 'white',
                fontWeight: '700',
                border: '3px solid rgba(255,255,255,0.3)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                position: 'relative',
                zIndex: 1,
              }}>
                {name ? name.charAt(0).toUpperCase() : '👤'}
              </div>
              
              {isEditingName ? (
                <div style={{ maxWidth: '280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                  <input
                    type="text"
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    placeholder="Enter your name"
                    className="edit-name-input"
                    autoFocus
                    style={{
                      textAlign: 'center',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.5)',
                      color: 'white',
                      borderRadius: '12px',
                      padding: '10px 16px',
                      width: '100%',
                      fontSize: '16px',
                      boxSizing: 'border-box',
                      fontWeight: '600',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '12px' }}>
                    <button
                      className="btn small primary"
                      type="button"
                      onClick={handleSaveName}
                      disabled={!!status}
                      style={{
                        background: 'white',
                        color: 'var(--primary)',
                        border: 'none',
                        padding: '8px 20px',
                        borderRadius: '10px',
                        fontWeight: '700',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      {status || '✓ Save'}
                    </button>
                    <button
                      className="btn small ghost"
                      type="button"
                      onClick={handleCancelEditName}
                      style={{
                        background: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.5)',
                        padding: '8px 20px',
                        borderRadius: '10px',
                        fontWeight: '600',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  {error && (
                    <p style={{
                      color: '#fca5a5',
                      fontSize: '13px',
                      marginTop: '8px',
                      background: 'rgba(255,255,255,0.15)',
                      padding: '6px 12px',
                      borderRadius: '8px',
                    }}>
                      {error}
                    </p>
                  )}
                </div>
              ) : (
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    fontSize: '24px',
                    fontWeight: '800',
                    color: 'white',
                    marginBottom: '6px',
                    letterSpacing: '-0.02em',
                  }}>
                    {name || 'Guest User'}
                  </div>
                  <div style={{
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.9)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}>
                    <span style={{ fontSize: '16px' }}>📱</span>
                    <span style={{ fontWeight: '500' }}>{phone || 'Not available'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleStartEditName}
                    style={{
                      padding: '9px 22px',
                      borderRadius: '24px',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.5)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    ✏️ Edit Name
                  </button>
                </div>
              )}
            </div>

            {/* Quick Actions Menu - Moved below name card */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '24px',
            }}>
              <button
                onClick={() => navigate(ROUTES.ORDERS)}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <svg style={{ width: '28px', height: '28px', color: '#ff6b35' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                  <path d="M9 14h6M9 18h6"/>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Orders</span>
              </button>
              
              <button
                onClick={onHelp}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <svg style={{ width: '28px', height: '28px', color: '#ff6b35' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Help</span>
              </button>
            </div>

            {/* Section header with improved styling */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid var(--border-light)',
            }}>
              <div>
                <h3 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.01em',
                }}>
                  📍 Your Addresses
                </h3>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  fontWeight: '500',
                }}>
                  Manage your delivery locations
                </p>
              </div>
              <div style={{
                background: 'var(--primary-bg)',
                color: 'var(--primary)',
                padding: '6px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '700',
              }}>
                {addresses.length}/10
              </div>
            </div>
            
            {addresses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {addresses.slice(0, 10).map((address, index) => {
                  const locationName = address.location || '';
                  const addressIcons = {
                    'Home': '🏠',
                    'Work': '💼',
                    'Shop': '🏪'
                  };
                  const icon = addressIcons[address.label] || '📍';
                  
                  return (
                    <div
                      key={`saved-${index}`}
                      style={{
                        background: 'linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)',
                        borderRadius: '16px',
                        border: '1.5px solid var(--border-light)',
                        padding: '18px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(255,90,31,0.12), 0 2px 4px rgba(0,0,0,0.06)';
                        e.currentTarget.style.borderColor = 'var(--primary-light)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)';
                        e.currentTarget.style.borderColor = 'var(--border-light)';
                      }}
                    >
                      {/* Decorative accent */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, var(--primary) 0%, var(--primary-dark) 100%)',
                        opacity: 0.6,
                      }} />
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            background: 'var(--primary-bg)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            flexShrink: 0,
                          }}>
                            {icon}
                          </div>
                          <div>
                            <span style={{
                              fontSize: '17px',
                              fontWeight: '700',
                              color: 'var(--text-primary)',
                              display: 'block',
                              letterSpacing: '-0.01em',
                            }}>
                              {address.label || 'Address'}
                            </span>
                            {locationName && (
                              <span style={{
                                fontSize: '12px',
                                color: 'var(--primary)',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '2px',
                              }}>
                                <span style={{ fontSize: '10px' }}>📍</span>
                                {locationName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            type="button"
                            onClick={() => handleEditAddress(index)}
                            style={{
                              background: 'var(--primary-bg)',
                              color: 'var(--primary)',
                              border: '1.5px solid transparent',
                              borderRadius: '10px',
                              padding: '8px 14px',
                              fontSize: '13px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--primary)';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'var(--primary-bg)';
                              e.currentTarget.style.color = 'var(--primary)';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <span>✏️</span>
                            <span>Edit</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAddress(index)}
                            style={{
                              background: '#fee2e2',
                              color: '#ef4444',
                              border: '1.5px solid transparent',
                              borderRadius: '10px',
                              padding: '8px 12px',
                              fontSize: '18px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#ef4444';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fee2e2';
                              e.currentTarget.style.color = '#ef4444';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--text-secondary)',
                        lineHeight: '1.7',
                        paddingLeft: '56px',
                        fontWeight: '500',
                      }}>
                        {[address.line1, address.line2, address.city, address.pincode]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '48px 24px',
                background: 'linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%)',
                borderRadius: '16px',
                border: '2px dashed var(--border-default)',
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'var(--primary-bg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '36px',
                }}>
                  📍
                </div>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'var(--text-primary)',
                }}>
                  No addresses yet
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: 'var(--text-muted)',
                  fontWeight: '500',
                }}>
                  Add your first delivery address to get started
                </p>
              </div>
            )}
            
            <div style={{ marginTop: '20px' }}>
              <button
                type="button"
                onClick={addAddress}
                disabled={addresses.length >= 10}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: '2px dashed var(--primary)',
                  background: 'var(--primary-bg)',
                  color: 'var(--primary)',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: addresses.length >= 10 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: addresses.length >= 10 ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (addresses.length < 10) {
                    e.currentTarget.style.background = 'var(--primary)';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderStyle = 'solid';
                  }
                }}
                onMouseLeave={(e) => {
                  if (addresses.length < 10) {
                    e.currentTarget.style.background = 'var(--primary-bg)';
                    e.currentTarget.style.color = 'var(--primary)';
                    e.currentTarget.style.borderStyle = 'dashed';
                  }
                }}
              >
                <span style={{ fontSize: '20px' }}>+</span>
                <span>Add New Address</span>
              </button>
              {addresses.length >= 10 && (
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: '13px',
                  color: '#ef4444',
                  textAlign: 'center',
                  fontWeight: '600',
                }}>
                  ⚠️ Maximum 10 addresses allowed
                </p>
              )}
            </div>

            <div style={{
              marginTop: '28px',
              paddingTop: '24px',
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              borderTop: '2px solid var(--border-light)',
            }}>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '14px',
                  border: '2px solid #fca5a5',
                  background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                  color: '#dc2626',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 4px rgba(220,38,38,0.15)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#dc2626';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)';
                  e.currentTarget.style.color = '#dc2626';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(220,38,38,0.15)';
                }}
              >
                <span style={{ fontSize: '20px' }}>🚪</span>
                <span>Logout from Account</span>
              </button>
              <div style={{ height: '96px', flexShrink: 0 }} aria-hidden="true" />
            </div>
          </div>
        )}

        {/* Addresses Tab */}
        {activeTab === 'addresses' && (
          <div style={{ padding: '0', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Section header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b', letterSpacing: '-0.3px' }}>
                  Delivery Addresses
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  {addresses.length} of 10 saved
                </p>
              </div>
              <button
                type="button"
                onClick={addAddress}
                disabled={addresses.length >= 10 || isAddingAddress}
                style={{
                  background: 'linear-gradient(135deg, #ff5a1f 0%, #e14a12 100%)', color: '#fff', border: 'none',
                  borderRadius: 12, padding: '11px 18px',
                  fontSize: 14, fontWeight: 700, cursor: addresses.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: addresses.length >= 10 ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 4px 12px rgba(255,90,31,0.25)',
                  transition: 'all 0.2s',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add New
              </button>
            </div>

            {/* Address cards */}
            {addresses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {addresses.slice(0, 10).map((address, index) => {
                  const locationName = address.location || '';
                  const addressIcons = { 'Home': '🏠', 'Work': '💼', 'Shop': '🏪' };
                  const icon = addressIcons[address.label] || '📍';

                  return (
                    <div
                      key={`addr-${index}`}
                      style={{
                        background: '#fff', borderRadius: 16,
                        border: '1px solid #e2e8f0', padding: '16px 16px 14px',
                        position: 'relative', overflow: 'hidden',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      }}
                    >
                      {/* Left accent bar */}
                      <div style={{
                        position: 'absolute', top: 12, left: 0, width: 3, height: 'calc(100% - 24px)',
                        background: 'linear-gradient(to bottom, #ff5a1f, #ff8a5b)', borderRadius: '0 3px 3px 0',
                      }} />

                      {/* Top row: icon + label + location */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, paddingLeft: 4 }}>
                        <div style={{
                          width: 42, height: 42, borderRadius: 12,
                          background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 20, flexShrink: 0, border: '1px solid #ffedd5',
                        }}>
                          {icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', letterSpacing: '-0.2px' }}>
                            {address.label || 'Address'}
                          </div>
                          {locationName && (
                            <div style={{ fontSize: 12, color: '#ff5a1f', fontWeight: 600, marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="#ff5a1f" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                              {locationName}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Address text */}
                      <div style={{
                        fontSize: 13, color: '#64748b', lineHeight: 1.6,
                        paddingLeft: 58, marginBottom: 12,
                      }}>
                        {[address.line1, address.line2, address.city, address.pincode].filter(Boolean).join(', ')}
                      </div>

                      {/* Action buttons — full width row */}
                      <div style={{ display: 'flex', gap: 8, paddingLeft: 58 }}>
                        <button
                          type="button"
                          onClick={() => handleEditAddress(index)}
                          style={{
                            flex: 1, background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
                            borderRadius: 10, padding: '9px 12px', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            transition: 'all 0.15s',
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAddress(index)}
                          style={{
                            background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca',
                            borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            transition: 'all 0.15s',
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Tip card when few addresses */}
                {addresses.length < 3 && (
                  <div style={{
                    background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12,
                    padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>💡</span>
                    <p style={{ margin: 0, fontSize: 13, color: '#0369a1', lineHeight: 1.5, fontWeight: 500 }}>
                      Add multiple addresses for home, work, or family — switch between them easily at checkout.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                textAlign: 'center', padding: '56px 24px',
                background: 'linear-gradient(to bottom, #fff 0%, #f8fafc 100%)', borderRadius: 20,
                border: '2px dashed #cbd5e1',
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%', background: '#fff7ed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px', fontSize: 32, border: '2px solid #ffedd5',
                }}>
                  📍
                </div>
                <h4 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
                  No addresses yet
                </h4>
                <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b', lineHeight: 1.5 }}>
                  Add your delivery address to start ordering
                </p>
                <button
                  type="button"
                  onClick={addAddress}
                  style={{
                    background: 'linear-gradient(135deg, #ff5a1f 0%, #e14a12 100%)', color: '#fff',
                    border: 'none', borderRadius: 12, padding: '13px 24px',
                    fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(255,90,31,0.3)',
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18 }}>+</span> Add Your First Address
                </button>
              </div>
            )}

            {addresses.length >= 10 && (
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>⚠️</span>
                <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>Maximum 10 addresses reached</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', maxWidth: '100vw' }}>

            {/* Orders list */}
            <div className="orders-scroll-area" style={{ padding: '16px', paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))', overflowX: 'hidden', overflowY: 'auto' }}>
              {/* Orders header — centered with glow */}
              <div style={{
                textAlign: 'center',
                marginBottom: 20, paddingBottom: 16,
              }}>
                <h2 style={{
                  margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b',
                  letterSpacing: '-0.5px',
                }}>
                  My Orders
                </h2>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  marginTop: 10, padding: '8px 20px',
                  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                  borderRadius: 24, border: '1px solid #fed7aa',
                  boxShadow: '0 0 16px rgba(255,90,31,0.15), 0 2px 6px rgba(255,90,31,0.1)',
                }}>
                  <span style={{ fontSize: 18 }}>📦</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: '#ea580c' }}>
                    {allOrders.length}
                  </span>
                  <span style={{ fontSize: 13, color: '#9a3412', fontWeight: 500 }}>
                    order{allOrders.length !== 1 ? 's' : ''} placed
                  </span>
                </div>
              </div>

              {allOrders.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {allOrders.map((order, index) => {
                const createdDate = order.createdAt?.toDate?.() || new Date();
                const STATUS = {
                  pending:          { label: 'Pending',          color: '#d97706', bg: '#fffbeb', border: '#fcd34d', dot: '#f59e0b' },
                  confirmed:        { label: 'Confirmed',        color: '#16a34a', bg: '#f0fdf4', border: '#86efac', dot: '#22c55e' },
                  shipped:          { label: 'Packed',           color: '#2563eb', bg: '#eff6ff', border: '#93c5fd', dot: '#3b82f6' },
                  'out-for-delivery': { label: 'Out for Delivery', color: '#0369a1', bg: '#f0f9ff', border: '#7dd3fc', dot: '#0ea5e9' },
                  delivered:        { label: 'Delivered',        color: '#16a34a', bg: '#f0fdf4', border: '#86efac', dot: '#22c55e' },
                  cancelled:        { label: 'Cancelled',        color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', dot: '#ef4444' },
                };
                const s = STATUS[order.status] || STATUS.pending;
                const itemNames = order.items?.slice(0, 3).map(i => i.name || i.productName || 'Item').join(', ');

                return (
                  <div key={order.id} onClick={() => setTrackingOrder(order)}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0',
                      borderLeft: '3px solid var(--primary)',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ padding: '14px 16px' }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' }}>
                            #{order.id.substring(0, 8).toUpperCase()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            {createdDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · {createdDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          padding: '5px 10px', borderRadius: '20px',
                          background: s.bg, fontSize: '11px', fontWeight: '700', color: s.color,
                        }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: s.dot }} />
                          {s.label}
                        </div>
                      </div>

                      {/* Items summary */}
                      <div style={{ fontSize: '13px', color: '#475569', marginBottom: '10px', fontWeight: '500' }}>
                        {itemNames} · {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
                      </div>

                      {/* Footer */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '17px', fontWeight: '800', color: '#1e293b' }}>
                          ₹{order.total || 0}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setTrackingOrder(order); }}
                          style={{
                            background: 'linear-gradient(135deg, #ff5a1f 0%, #e14a12 100%)',
                            color: 'white', border: 'none', borderRadius: '10px',
                            padding: '8px 16px', fontSize: '13px', fontWeight: '700',
                            cursor: 'pointer', boxShadow: '0 2px 8px rgba(255,90,31,0.25)',
                          }}
                        >
                          Track order
                        </button>
                      </div>
                    </div>
                  </div>
                );
                  })}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center', padding: '60px 20px',
                  background: 'white', borderRadius: 'var(--radius-xl)',
                  border: '1px solid var(--border-light)',
                }}>
                  <div style={{
                    width: '80px', height: '80px', borderRadius: '50%',
                    background: 'var(--primary-bg)', margin: '0 auto 16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '36px',
                  }}>🛍️</div>
                  <div style={{ fontSize: '17px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '6px' }}>No orders yet</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>Your order history will appear here</div>
                </div>
              )}
            </div>
          </div>
        )}

        {trackingOrder && (
          <OrderTracking order={trackingOrder} onClose={() => {
            setTrackingOrder(null);
            if (onClearTrackingId) onClearTrackingId();
          }} />
        )}

        {/* Address Picker */}
        {isAddingAddress && (
          <AddressPicker
            initialAddress={editingIndex !== null ? addresses[editingIndex] : undefined}
            onSave={handlePickerSave}
            onClose={handleCloseAddAddress}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmIndex !== null && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              animation: 'slideUp 0.3s ease-out'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '32px'
                }}>
                  ⚠️
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Delete Address?
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  Are you sure you want to delete this address? This action cannot be undone.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setDeleteConfirmIndex(null)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-default)',
                    background: 'white',
                    color: 'var(--text-primary)',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAddress}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '32px'
                }}>
                  🚪
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Logout?
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  Are you sure you want to logout?
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-default)',
                    background: 'white',
                    color: 'var(--text-primary)',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePanel;
